import { useCallback, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { type FileRejection, useDropzone } from 'react-dropzone';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Button } from 'components/DesignSystem/Button';
import classes from 'components/widgets/ImageUploader/component/Dropzone/Dropzone.module.scss';
import { DropzoneUploadError } from 'components/widgets/ImageUploader/component/Dropzone/DropzoneUploadError/DropzoneUploadError';

interface Props {
    onImageUpload?: (file: File) => void | Promise<void>;
}

const rejectionMessage = (fileRejections: FileRejection[]): string => {
    const first = fileRejections[0];
    if (!first?.errors?.length) {
        return 'This file could not be uploaded.';
    }
    const { code, message } = first.errors[0];
    switch (code) {
        case 'file-invalid-type':
            return 'Please choose an image file (for example PNG or JPEG).';
        case 'file-too-large':
            return 'This file is too large. Please choose a smaller image.';
        case 'file-too-small':
            return 'This file is too small.';
        case 'too-many-files':
            return 'Please upload only one image at a time.';
        default:
            return message || 'Upload failed. Please try again.';
    }
};

const Dropzone = ({ onImageUpload }: Props) => {
    const [uploadError, setUploadError] = useState<string | null>(null);

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const file = acceptedFiles[0];
            if (!file) {
                setUploadError(
                    fileRejections.length > 0
                        ? rejectionMessage(fileRejections)
                        : 'No image file was received. Please try again.',
                );
                return;
            }
            setUploadError(null);
            try {
                onImageUpload?.(file);
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Something went wrong while uploading. Please try again.';
                setUploadError(message);
            }
        },
        [onImageUpload],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onError: () => {
            setUploadError('Could not read this file. Please try again.');
        },
        accept: {
            'image/*': [],
        },
        multiple: false,
    });

    const rootClassName = [
        classes.root,
        isDragActive ? classes.rootDragActive : '',
        uploadError ? classes.rootError : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Box {...getRootProps({ className: rootClassName })}>
            <input {...getInputProps()} />

            {uploadError ? (
                <DropzoneUploadError message={uploadError} onTryAgain={() => setUploadError(null)} />
            ) : (
                <>
                    <span className={classes.icon} aria-hidden>
                        <MuiIcon
                            icon={'cloud_upload'}
                            type={'outlined'}
                            fontSize={'5rem'}
                            color={'var(--text-brand-primary)'}
                        />
                    </span>

                    <Typography component={'p'} className={classes.title}>
                        Drag and drop an image here
                    </Typography>

                    <Typography component={'p'} className={classes.subtitle}>
                        Or click to browse from your device
                    </Typography>

                    <div className={classes.buttonUploadFileWrapper}>
                        <Button
                            type={'button'}
                            variant={'secondary'}
                            startIcon={
                                <MuiIcon icon={'folder'} type={'outlined'} color={'inherit'} fontSize={'16px'} />
                            }
                        >
                            <span className={classes.buttonUploadText}>Browse files</span>
                        </Button>
                    </div>
                </>
            )}
        </Box>
    );
};

export default Dropzone;
